import React, { useContext, useState } from "react";
import { Button, message, Modal, Select, Space, Descriptions, Tag } from "antd";
import CustomTable from "../../components/CustomTable";
import useApi from "../../hooks/useApi";
import useFilter from "../../hooks/useFilter";
import { AppStateContext } from "../../context/AppContext";
import {
  acceptSubscriptionRequest,
  ListSubscriptionRequest,
  rejectSubscriptionRequest,
  updateSubscriptionRequest,
} from "../../constants/API/API";
import moment from "moment";
import { CheckOutlined, EditOutlined, EyeOutlined, IdcardOutlined } from "@ant-design/icons";

const REQUEST_STATUS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];
const REQUEST_TYPE = [
  { value: "approval", label: "Approval" },
  { value: "cancellation", label: "Cancellation" },
];

// Helper function to convert data to CSV
const convertToCSV = (data) => {
  if (!data || data.length === 0) return "";

  const headers = [
    "User Name",
    "User Type",
    "Request Type",
    "Subscription Name",
    "Start Date",
    "End Date",
    "Status",
    "Requested On",
  ];

  const rows = data.map((record) => [
    `"${record?.userId?.name || "-"}"`,
    `"${record?.userId?.roleType || "-"}"`,
    `"${record.requestType || "-"}"`,
    `"${record?.subscriptionId?.name || "-"}"`,
    `"${moment(record.startDate).format("ll")}"`,
    `"${moment(record.endDate).format("ll")}"`,
    `"${record.requestStatus || "-"}"`,
    `"${
      record.createdAt ? new Date(record.createdAt).toLocaleDateString() : "-"
    }"`,
  ]);

  return [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
};

// Helper function to trigger CSV download
const downloadCSV = (csvData, filename) => {
  const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const SubscriptionRequest = () => {
  const { setPageHeading } = useContext(AppStateContext);
  setPageHeading("Subscription Request Management");

  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(new Date().getTime());
  const [data, setData] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [filters, setFilters] = useFilter({
    sortField: "createdOn",
    sort: "desc",
    studentId: undefined,
    classId: undefined,
    branchId: undefined,
    payment_Status: undefined,
  });

  const [decisionModalVisible, setDecisionModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const showDecisionPopup = (record) => {
    setSelectedRecord(record);
    setDecisionModalVisible(true);
  };

  const showViewPopup = (record) => {
    setSelectedRecord(record);
    setViewModalVisible(true);
  };

  const handleDecision = async (status) => {
    try {
      if (status === "accept") {
        await acceptSubscriptionRequest(selectedRecord?._id);
      } else {
        await rejectSubscriptionRequest(selectedRecord?._id);
      }
      message.success(`Request ${status} successfully`);
      setDecisionModalVisible(false);
      setRefresh(new Date().getTime());
    } catch (error) {
      message.error(`Failed to ${status.toLowerCase()} request`);
    }
  };

  useApi({
    callback: ListSubscriptionRequest,
    payload: filters,
    dependencies: [refresh, filters],
    shouldCallApi: true,
    setLoading,
    onSuccess: ({ list, totalDocs }) => {
      setData(list);
      setTotalDocs(totalDocs);
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  const handleFilterChange = (key) => (value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  // Handle CSV export
  const handleExport = () => {
    if (data.length === 0) {
      message.warning("No data available to export");
      return;
    }

    const csvData = convertToCSV(data);
    const filename = `subscription_requests_${moment().format(
      "YYYYMMDD_HHmmss"
    )}.csv`;
    downloadCSV(csvData, filename);
    message.success("Report exported successfully");
  };

  const renderFilters = () => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
      <Space>
        <Select
          allowClear
          style={{ width: 200 }}
          placeholder="Filter by Request Status"
          onChange={handleFilterChange("requestStatus")}
          value={filters.requestStatus}
          options={REQUEST_STATUS}
        />
        <Select
          allowClear
          style={{ width: 200 }}
          placeholder="Filter by Request Type"
          onChange={handleFilterChange("requestType")}
          value={filters.requestType}
          options={REQUEST_TYPE}
        />
        <Button type="primary" onClick={handleExport}>
          Export Report
        </Button>
      </Space>
    </div>
  );

  const columns = [
    {
      title: "User Name",
      dataIndex: "userId",
      key: "userId",
      render: (text, record) => record?.userId?.name || "-",
    },
    {
      title: "User Type",
      dataIndex: "userId",
      key: "userType",
      render: (text, record) => record?.userId?.roleType || "-",
    },
    {
      title: "Request Type",
      dataIndex: "requestType",
      key: "requestType",
    },
    {
      title: "Subscription Name",
      dataIndex: "subscriptionId",
      key: "subscriptionId",
      render: (text, record) => record?.subscriptionId?.name || "-",
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      render: (text, record) => moment(record.startDate).format("ll"),
    },
    // {
    //   title: "End Date",
    //   dataIndex: "endDate",
    //   key: "endDate",
    //   render: (text, record) => moment(record.endDate).format("ll"),
    // },
    {
      title: "Status",
      dataIndex: "requestStatus",
      key: "requestStatus",
      render: (status) => {
        let color;

        switch (status) {
          case "Pending":
            color = "orange";
            break;
          case "Approved":
            color = "green";
            break;
          case "Rejected":
            color = "red";
            break;
          default:
            color = "blue";
        }

        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Requested On",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => (text ? new Date(text).toLocaleDateString() : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            onClick={() => showViewPopup(record)}
            icon={<EyeOutlined />}
            title="View"
          />
          {record.requestStatus === "pending" && (
            <Button
              type="primary"
              onClick={() => showDecisionPopup(record)}
              icon={<EditOutlined />}
              title="Edit Status"
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <CustomTable
        data={data}
        loading={loading}
        filters={filters}
        setFilters={setFilters}
        columns={columns}
        totalDocs={totalDocs}
        searchField={["userName", "subscriptionName"]}
        searchPlaceholder={"Search by User Name or Subscription Name"}
        customFilter={renderFilters}
        hideAddButton
      />

      <Modal
        open={decisionModalVisible}
        onCancel={() => setDecisionModalVisible(false)}
        footer={null}
        className="tab_modal"
        centered
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              background: "#fff7e6",
              borderRadius: "50%",
              width: 64,
              height: 64,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <CheckOutlined style={{ color: "#fa8c16", fontSize: 32 }} />
          </div>
          <p style={{ fontWeight: 500, fontSize: 18, marginBottom: 24 }}>
            Are you sure you want to update the status of this subscription
            request?
          </p>
          <div className="main-new-rejects-bnuton">
            <Button onClick={() => handleDecision("accept")}>Accept</Button>
            <Button onClick={() => handleDecision("reject")}>Reject</Button>
            <Button onClick={() => setDecisionModalVisible(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        visible={viewModalVisible}
        title="Subscription Request Details"
        onCancel={() => setViewModalVisible(false)}
        footer={null}
        className="tab_modal"
        width={700}
      >
        {selectedRecord && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="User Name">
              {selectedRecord?.userId?.name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="User Type">
              {selectedRecord?.userId?.roleType || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Request Type">
              {selectedRecord.requestType || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Subscription Name">
              {selectedRecord?.subscriptionId?.name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Start Date">
              {moment(selectedRecord.startDate).format("ll")}
            </Descriptions.Item>
            <Descriptions.Item label="End Date">
              {moment(selectedRecord.endDate).format("ll")}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {selectedRecord.requestStatus || "-"}
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <>
                  <IdcardOutlined /> Subscription Request ID
                </>
              }
            >
              <Tag style={{ fontFamily: "monospace", fontSize: "13px" }}>
                {selectedRecord.uid}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Requested On">
              {selectedRecord.createdAt
                ? moment(selectedRecord.createdAt).format("ll")
                : "-"}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

export default SubscriptionRequest;
